import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FeatureFlags, FeatureFlagService } from '../../services/feature-flag.service';

@Component({
    selector: 'app-feature-flag-dashboard',
    standalone: true,
    templateUrl: './feature-flag-dashboard.component.html',
    styleUrls: ['./feature-flag-dashboard.component.less'],
    imports: [
        DecimalPipe,
        TitleCasePipe
    ]
})
export class FeatureFlagDashboardComponent implements OnInit {
  flags: FeatureFlags = {} as FeatureFlags;
  migrationProgress = 0;

  flagCategories = [
    {
      name: 'Router Migration',
      flags: [
        { key: 'useAngularRouter', label: 'Global Angular Router', description: 'Master switch for Angular Router usage' },
        { key: 'useAngularRouterAuth', label: 'Authentication Routes', description: 'Use Angular Router for login, SSO, and auth routes' },
        { key: 'useAngularRouterNetwork', label: 'Network Routes', description: 'Use Angular Router for network entity management' },
        { key: 'useAngularRouterDashboard', label: 'Dashboard Routes', description: 'Use Angular Router for dashboard and activity views' },
        { key: 'useAngularRouterProfile', label: 'Profile Routes', description: 'Use Angular Router for profile management' },
        { key: 'useAngularRouterAdmin', label: 'Admin Routes', description: 'Use Angular Router for admin and configuration' },
        { key: 'useAngularRouterWorkflows', label: 'Workflow Routes', description: 'Use Angular Router for filing and interview workflows' },
        { key: 'useAngularRouterContracts', label: 'Contract Routes', description: 'Use Angular Router for contract management' },
        { key: 'useAngularRouterSignup', label: 'Signup Routes', description: 'Use Angular Router for signup and invitation flows' }
      ]
    },
    {
      name: 'Safety & Monitoring',
      flags: [
        { key: 'enableRoutePerformanceMonitoring', label: 'Performance Monitoring', description: 'Track routing performance metrics' },
        { key: 'enableRouteLogging', label: 'Route Logging', description: 'Log all route transitions for debugging' },
        { key: 'enableFallbackToUIRouter', label: 'Fallback to UI-Router', description: 'Fallback to UI-Router on Angular Router errors' },
        { key: 'enableRouteValidation', label: 'Route Validation', description: 'Validate routes before navigation' }
      ]
    },
    {
      name: 'Development Tools',
      flags: [
        { key: 'showMigrationDebugInfo', label: 'Debug Information', description: 'Show migration debug info in console' },
        { key: 'enableRouteComparison', label: 'Route Comparison', description: 'Compare UI-Router vs Angular Router behavior' },
        { key: 'logRouteTransitions', label: 'Transition Logging', description: 'Detailed logging of route transitions' }
      ]
    }
  ];

  migrationPhases = [
    { phase: 0, name: 'UI-Router Only', description: 'All routes use UI-Router' },
    { phase: 1, name: 'Infrastructure Ready', description: 'Angular Router enabled, monitoring active' },
    { phase: 2, name: 'Authentication Migrated', description: 'Login and auth routes use Angular Router' },
    { phase: 3, name: 'Core Features Migrated', description: 'Network, dashboard, and profile routes migrated' },
    { phase: 4, name: 'Admin Migrated', description: 'Admin and configuration routes migrated' },
    { phase: 5, name: 'Full Migration', description: 'All routes use Angular Router' }
  ];

  constructor(
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.featureFlagService.getFlags().subscribe(flags => {
      this.flags = flags;
      this.migrationProgress = this.featureFlagService.getMigrationProgress();
    });
  }

  toggleFlag(flagKey: keyof FeatureFlags): void {
    this.featureFlagService.toggle(flagKey);
  }

  enablePhase(phase: number): void {
    this.featureFlagService.enableProgressiveMigration(phase);
  }

  resetToDefaults(): void {
    if (confirm('Are you sure you want to reset all feature flags to defaults?')) {
      this.featureFlagService.resetToDefaults();
    }
  }

  exportConfiguration(): void {
    const config = this.featureFlagService.exportConfiguration();
    const blob = new Blob([config], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `feature-flags-${Date.now()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  importConfiguration(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (this.featureFlagService.importConfiguration(content)) {
          alert('Configuration imported successfully!');
        } else {
          alert('Failed to import configuration. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  }

  getCurrentRouterType(category: string): string {
    return this.featureFlagService.getRouterTypeForCategory(category);
  }

  getPhaseStatus(phase: number): 'active' | 'completed' | 'pending' {
    const currentProgress = this.migrationProgress;

    if (phase === 0 && currentProgress === 0) return 'active';
    if (phase === 1 && this.flags.useAngularRouter && !this.flags.useAngularRouterAuth) return 'active';
    if (phase === 2 && this.flags.useAngularRouterAuth && !this.flags.useAngularRouterNetwork) return 'active';
    if (phase === 3 && this.flags.useAngularRouterNetwork && !this.flags.useAngularRouterAdmin) return 'active';
    if (phase === 4 && this.flags.useAngularRouterAdmin && !this.flags.useAngularRouterWorkflows) return 'active';
    if (phase === 5 && currentProgress === 100) return 'active';

    if (currentProgress >= (phase / 5) * 100) return 'completed';

    return 'pending';
  }

  getFlagCount(category: any): { enabled: number; total: number } {
    const enabled = category.flags.filter((flag: any) => this.flags[flag.key]).length;
    return { enabled, total: category.flags.length };
  }

  /**
   * Helper method to safely get flag value by key
   */
  getFlagValue(flagKey: string): boolean {
    return this.flags[flagKey as keyof FeatureFlags] || false;
  }

  /**
   * Helper method to safely toggle flag by key
   */
  toggleFlagByKey(flagKey: string): void {
    this.toggleFlag(flagKey as keyof FeatureFlags);
  }
}
